import { useState } from 'react';
import { useMemoizedFn } from 'ahooks';
import { notification } from 'tea-component';
import { reportOperationLog } from '@gp/services/global';
// @ts-ignore
import { captureNode } from '@gp/utils/utils';
import { downloadFileByBlob, stream2Object, getDownLoadCSVResponseHeaderFileName } from '@gp/utils/common';
import { FILE_TYPES_MAP } from '@gp/constants/const';
import { ALL_REPORT_OPERATION_LOG } from '@gp/constants/operationLogParams';

interface IDownloadServiceRes {
  // 文件流
  stream: BlobPart;
  // 文件名
  fileName: string;
  [key: string]: any;
}

// 构建下载服务，接受下载入参，按照 IDownloadServiceRes格式 返回数据
export function createDownloadService(service: (params: any) => Promise<any>) {
  return async (params: any): Promise<IDownloadServiceRes> => {
    try {
      const { headers, data, status } = await service(params);
      if (status !== 200) {
        return Promise.reject(new Error('download error'));
      }
      const fileName = getDownLoadCSVResponseHeaderFileName(headers);
      return Promise.resolve({ stream: data, fileName });
    } catch (exception) {
      console.warn(exception);
      return Promise.reject(exception);
    }
  };
}

/**
 * 下载hook，包含CSV、EXCEL、PDF三种格式下载
 * 返回isDownloading下载loading状态，handleDownload下载处理函数
 */
export default function useDownload(options: {
  // 文件名，仅用于PDF下载，CSV、EXCEL下载均使用接口返回的文件名（响应头中获取）
  fileName?: string;
  // PDF下载的打点key
  pdfDownloadLogKey?: string;
  // CSV下载的打点key
  csvDownloadLogKey?: string;
  // EXCEL下载的打点key
  excelDownloadLogKey?: string;
  // 需要下载的UI视图的Node节点
  node?: Element | null;
  // CSV、EXCEL下载的接口
  downloadService?: (params: any) => Promise<IDownloadServiceRes>;
  nodeOptions?: { [keyName: string]: string; };
}) {
  const fileName = options?.fileName || 'file';
  const node = options?.node;
  const nodeOptions = options?.nodeOptions;
  const downloadService = options?.downloadService;
  const pdfDownloadLogKey = options?.pdfDownloadLogKey || '';
  const csvDownloadLogKey = options?.csvDownloadLogKey || '';
  const excelDownloadLogKey = options?.excelDownloadLogKey || '';

  const [isDownloading, setIsDownloading] = useState(false);

  const getDownloadLogKey = useMemoizedFn((fileType): string => {
    const map = {
      [FILE_TYPES_MAP.CSV]: csvDownloadLogKey,
      [FILE_TYPES_MAP.EXCEL]: excelDownloadLogKey,
      [FILE_TYPES_MAP.PDF]: pdfDownloadLogKey,
    };
    return map[fileType];
  });

  const handleDownloadPDF = useMemoizedFn(async () => {
    await captureNode(fileName, node, nodeOptions);
  });

  const handleDownloadCSVOrExcel = useMemoizedFn(async (params) => {
    try {
      if (!(downloadService instanceof Function)) return;
      const { stream, fileName } = await downloadService(params);
      downloadFileByBlob(stream, fileName);
    } catch (res: any) {
      const resData = await stream2Object(res?.data);
      throw new Error(resData?.msg || '');
    }
  });

  const handleDownload = useMemoizedFn(async (params) => {
    if (isDownloading) return;
    try {
      setIsDownloading(true);
      const { fileType, ...restParams } = params || {};
      const downloadLogKey = getDownloadLogKey(fileType);
      const downloadLogParams = ALL_REPORT_OPERATION_LOG[downloadLogKey];
      if (downloadLogParams) {
        reportOperationLog(downloadLogParams, 'button');
      }
      if ([FILE_TYPES_MAP.CSV, FILE_TYPES_MAP.EXCEL].includes(fileType)) {
        await handleDownloadCSVOrExcel(restParams);
      } else if (FILE_TYPES_MAP.PDF === fileType) {
        await handleDownloadPDF();
      }
      notification.success({
        unique: true,
        description: 'Download successfully',
      });
    } catch (exception: any) {
      console.warn(`download error: ${exception.message}`);
      notification.error({
        unique: true,
        description: 'Download failed',
      });
    } finally {
      setIsDownloading(false);
    }
  });

  return {
    isDownloading,
    setIsDownloading,
    handleDownload,
  };
}
